import { gql } from '@urql/core';

const ORDER_LIST_FRAGMENT = gql`
  fragment OrderList on Order {
    id
    code
    state
    total
    totalWithTax
    currencyCode
    orderPlacedAt
    createdAt
    customer {
      id
      firstName
      lastName
      emailAddress
      phoneNumber
    }
    shippingAddress {
      city
      country
      phoneNumber
    }
    lines {
      id
      quantity
      bundleId
      parentLineId
      productVariant {
        id
        name
        sku
        featuredAsset {
          preview
        }
        stockLevels {
          stockLocationId
        }
      }
    }
    payments {
      id
      method
      state
      amount
      transactionId
      metadata
      refunds {
        id
        total
        state
      }
    }
    financialSummary {
      status
      label
      paymentStatus
      refundStatus
      paidTotal
      authorizedTotal
      manualPendingTotal
      refundedTotal
      pendingRefundTotal
      outstandingTotal
      paymentMethods
      manualActionRequired
      canFulfill
    }
    fulfillments {
      id
      state
      trackingCode
      lines {
        orderLineId
        quantity
      }
      customFields {
        deliveryType
        pickupLocationName
        readyForPickupAt
      }
    }
    shippingLines {
      shippingMethod {
        id
        name
      }
      priceWithTax
    }
    customFields {
      assigneeId
      tags
      isGift
      giftWrapOptionName
      bankTransferExpiresAt
      salesChannelCode
      utmSource
    }
    attributionSnapshot {
      winningSource
      winningCode
      method
    }
    assignee {
      id
      firstName
      lastName
    }
  }
`;

const ORDER_DETAIL_FRAGMENT = gql`
  fragment OrderDetail on Order {
    id
    code
    state
    nextStates
    active
    total
    totalWithTax
    subTotal
    subTotalWithTax
    shipping
    shippingWithTax
    currencyCode
    orderPlacedAt
    createdAt
    updatedAt
    taxSummary {
      description
      taxRate
      taxBase
      taxTotal
    }
    customer {
      id
      firstName
      lastName
      emailAddress
      phoneNumber
    }
    shippingAddress {
      fullName
      company
      streetLine1
      streetLine2
      city
      province
      postalCode
      country
      phoneNumber
    }
    lines {
      id
      quantity
      unitPrice
      unitPriceWithTax
      linePrice
      linePriceWithTax
      discountedLinePrice
      discountedLinePriceWithTax
      proratedUnitPriceWithTax
      proratedLinePriceWithTax
      orderPlacedQuantity
      placedProratedLinePriceWithTax
      selectedCustomizations {
        fieldId
        fieldType
        fieldLabel
        optionId
        optionName
        textInput
        fileAssetId
        fileAssetPreview
        price
      }
      productVariant {
        id
        name
        sku
        featuredAsset {
          preview
        }
        product {
          id
          name
        featuredAsset {
            preview
          }
        }
      }
      bundleId
      parentLineId
    }
    payments {
      id
      method
      amount
      state
      transactionId
      metadata
      createdAt
      refunds {
        id
        total
        reason
        state
        lines {
          orderLineId
          quantity
        }
      }
    }
    financialSummary {
      status
      label
      paymentStatus
      refundStatus
      paidTotal
      authorizedTotal
      manualPendingTotal
      refundedTotal
      pendingRefundTotal
      outstandingTotal
      paymentMethods
      manualActionRequired
      canFulfill
    }
    fulfillments {
      id
      state
      nextStates
      method
      trackingCode
      createdAt
      lines {
        orderLineId
        quantity
      }
      customFields {
        deliveryType
        pickupLocationName
        readyForPickupAt
        shipmentId
      }
    }
    customFields {
      tags
      salesChannelCode
      assigneeId
      deliveryInstructions
      pickupLocationId
      utmSource
      utmMedium
      utmCampaign
      utmContent
      utmTerm
      landingPage
      httpReferrer
      isGift
      giftMessage
      giftSenderName
      giftRecipientName
      giftWrapOptionName
      bankTransferReceiptAssetId
    }
    attributionSnapshot {
      winningSource
      winningId
      winningCode
      method
      ruleVersion
      attributedAt
      resolvedBy
      signals
    }
    commissionTransaction {
      id
      state
      amount
      currencyCode
      affiliateId
      isCod
      approvedAt
      paidAt
      cancelledAt
    }
    assignee {
      id
      firstName
      lastName
    }
    billingAddress {
      fullName
      company
      streetLine1
      streetLine2
      city
      province
      postalCode
      country
      phoneNumber
    }
    shippingLines {
      id
      shippingMethod {
        id
        name
      }
      priceWithTax
    }
    couponCodes
    discounts {
      description
      amount
      amountWithTax
    }
    surcharges {
      id
      description
      sku
      price
      priceWithTax
      taxRate
    }
    paymentLink {
      id
      createdAt
      token
      orderId
      state
      expiresAt
      viewedAt
      usedAt
      url
    }
  }
`;

export const GET_ORDERS = gql`
  query GetOrders($options: OrderListOptions) {
    orders(options: $options) {
      items {
        ...OrderList
      }
      totalItems
    }
  }
  ${ORDER_LIST_FRAGMENT}
`;

export const GET_ORDERS_FILTERED = gql`
  query GetOrdersFiltered(
    $options: OrderListOptions,
    $customerSearch: OrderFilterExtended,
    $productSearch: ProductSearchInput,
    $trackingSearch: TrackingSearchInput,
    $stockLocationId: ID
  ) {
    ordersFiltered(
      options: $options,
      customerSearch: $customerSearch,
      productSearch: $productSearch,
      trackingSearch: $trackingSearch,
      stockLocationId: $stockLocationId
    ) {
      items {
        ...OrderList
      }
      totalItems
    }
  }
  ${ORDER_LIST_FRAGMENT}
`;

export const GET_ORDER_DETAIL = gql`
  query GetOrderDetail($id: ID!) {
    order: merchantOrder(id: $id) {
      ...OrderDetail
      history(options: { take: 50, sort: { createdAt: DESC } }) {
        items {
          id
          type
          createdAt
          data
          isPublic
          administrator {
            firstName
            lastName
          }
        }
        totalItems
      }
    }
  }
  ${ORDER_DETAIL_FRAGMENT}
`;

/**
 * Drafts are intentionally excluded from merchantOrder(), which is the
 * placed-order read boundary used by the normal order detail page. The draft
 * editor must use commerce kernel's channel-scoped Admin `order` query so a newly
 * created Draft remains editable without weakening placed-order visibility.
 */
export const GET_DRAFT_ORDER_DETAIL = gql`
  query GetDraftOrderDetail($id: ID!) {
    order(id: $id) {
      ...OrderDetail
      history(options: { take: 50, sort: { createdAt: DESC } }) {
        items {
          id
          type
          createdAt
          data
          isPublic
          administrator {
            firstName
            lastName
          }
        }
        totalItems
      }
    }
  }
  ${ORDER_DETAIL_FRAGMENT}
`;

export const GET_NEXT_ORDER_STATES = gql`
  query GetNextOrderStates($id: ID!) {
    order(id: $id) {
      id
      nextStates
    }
  }
`;

export const TRANSITION_ORDER_TO_STATE = gql`
  mutation TransitionOrderToState($id: ID!, $state: String!) {
    transitionOrderToState(id: $id, state: $state) {
      ... on Order {
        id
        state
        nextStates
      }
      ... on OrderStateTransitionError {
        errorCode
        message
        transitionError
        fromState
        toState
      }
    }
  }
`;

/**
 * P0-4: cancels the order together with its not-yet-picked-up carrier
 * waybills as one server operation. `requestKey` is one per merchant cancel
 * action, so a retried submit returns the first result instead of cancelling
 * twice.
 */
export const CANCEL_ORDER_WITH_WAYBILLS = gql`
  mutation CancelOrderWithWaybills($input: CancelOrderWithWaybillsInput!) {
    cancelOrderWithWaybills(input: $input) {
      success
      replayed
      cancelledTrackingCodes
      errorCode
      errorDetails
      order {
        id
        state
      }
    }
  }
`;

export const ADD_FULFILLMENT = gql`
  mutation AddFulfillment($input: FulfillOrderInput!) {
    addFulfillmentToOrder(input: $input) {
      ... on Fulfillment {
        id
        state
        method
        trackingCode
        lines {
          orderLineId
          quantity
        }
      }
      ... on EmptyOrderLineSelectionError {
        errorCode
        message
      }
      ... on ItemsAlreadyFulfilledError {
        errorCode
        message
      }
      ... on InsufficientStockOnHandError {
        errorCode
        message
        productVariantId
        productVariantName
        stockOnHand
      }
      ... on InvalidFulfillmentHandlerError {
        errorCode
        message
      }
      ... on FulfillmentStateTransitionError {
        errorCode
        message
      }
      ... on CreateFulfillmentError {
        errorCode
        message
        fulfillmentHandlerError
      }
    }
  }
`;

export const TRANSITION_FULFILLMENT_STATE = gql`
  mutation TransitionFulfillmentState($id: ID!, $state: String!) {
    transitionFulfillmentToState(id: $id, state: $state) {
      ... on Fulfillment {
        id
        state
        trackingCode
      }
      ... on FulfillmentStateTransitionError {
        errorCode
        message
        transitionError
        fromState
        toState
      }
    }
  }
`;

export const CONFIRM_PICKUP_COLLECTION = gql`
  mutation ConfirmPickupCollection(
    $fulfillmentId: ID!
    $pickupCode: String!
    $cashCollected: Boolean = false
  ) {
    confirmPickupCollection(
      fulfillmentId: $fulfillmentId
      pickupCode: $pickupCode
      cashCollected: $cashCollected
    ) {
      success
      errorCode
      errorDetails
      fulfillment {
        id
        state
      }
    }
  }
`;

export const SETTLE_PAYMENT = gql`
  mutation SettlePayment($id: ID!) {
    settlePayment(id: $id) {
      ... on Payment {
        id
        state
        amount
        method
      }
      ... on SettlePaymentError {
        errorCode
        message
        paymentErrorMessage
      }
      ... on PaymentStateTransitionError {
        errorCode
        message
        transitionError
      }
      ... on OrderStateTransitionError {
        errorCode
        message
        transitionError
      }
    }
  }
`;

export const CONFIRM_MANUAL_PAYMENT = gql`
  mutation ConfirmManualPayment($input: ConfirmManualPaymentInput!) {
    confirmManualPayment(input: $input) {
      id
      state
      amount
      method
      metadata
    }
  }
`;

export const CANCEL_PAYMENT = gql`
  mutation CancelPayment($id: ID!) {
    cancelPayment(id: $id) {
      ... on Payment {
        id
        state
      }
      ... on CancelPaymentError {
        errorCode
        message
        paymentErrorMessage
      }
      ... on PaymentStateTransitionError {
        errorCode
        message
      }
    }
  }
`;

export const REFUND_ORDER = gql`
  mutation RefundOrder($input: RefundOrderInput!) {
    refundOrder(input: $input) {
      ... on Refund {
        id
        total
        reason
        state
        transactionId
        paymentId
      }
      ... on QuantityTooGreatError {
        errorCode
        message
      }
      ... on NothingToRefundError {
        errorCode
        message
      }
      ... on OrderStateTransitionError {
        errorCode
        message
      }
      ... on MultipleOrderError {
        errorCode
        message
      }
      ... on PaymentOrderMismatchError {
        errorCode
        message
      }
      ... on RefundOrderStateError {
        errorCode
        message
        orderState
      }
      ... on AlreadyRefundedError {
        errorCode
        message
        refundId
      }
      ... on RefundAmountError {
        errorCode
        message
        maximumRefundable
      }
      ... on RefundStateTransitionError {
        errorCode
        message
      }
    }
  }
`;

export const ADD_ORDER_NOTE = gql`
  mutation AddOrderNote($input: AddNoteToOrderInput!) {
    addNoteToOrder(input: $input) {
      id
      history(options: { take: 1, sort: { createdAt: DESC } }) {
        items {
          id
          type
          createdAt
          data
          isPublic
          administrator {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const ADD_ORDER_COMMENT = gql`
  mutation AddOrderComment($input: AddOrderCommentInput!) {
    addOrderComment(input: $input) {
      id
      history(options: { take: 1, sort: { createdAt: DESC } }) {
        items {
          id
          type
          createdAt
          data
          isPublic
          administrator {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const DELETE_ORDER_NOTE = gql`
  mutation DeleteOrderNote($id: ID!) {
    deleteOrderNote(id: $id) {
      result
      message
    }
  }
`;

export const ASSIGN_ORDER = gql`
  mutation AssignOrder($orderId: ID!, $assigneeId: ID, $note: String) {
    assignOrder(orderId: $orderId, assigneeId: $assigneeId, note: $note) {
      success
      errorCode
      message
    }
  }
`;

export const GET_CHANNEL_STAFF = gql`
  query GetChannelStaff {
    channelStaff {
      id
      firstName
      lastName
      emailAddress
    }
  }
`;

export const PAYMENT_LINK_FRAGMENT = gql`
  fragment PaymentLinkFields on PaymentLink {
    id
    createdAt
    token
    orderId
    state
    expiresAt
    viewedAt
    usedAt
    url
  }
`;

export const CREATE_PAYMENT_LINK = gql`
  mutation CreatePaymentLink($orderId: ID!, $expiresInHours: Int) {
    createPaymentLink(orderId: $orderId, expiresInHours: $expiresInHours) {
      success
      paymentLink {
        ...PaymentLinkFields
      }
      errorCode
      message
    }
  }
  ${PAYMENT_LINK_FRAGMENT}
`;

export const GET_ELIGIBLE_DRAFT_ORDER_PAYMENT_METHODS = gql`
  query EligibleDraftOrderPaymentMethods($orderId: ID!) {
    eligibleDraftOrderPaymentMethods(orderId: $orderId) {
      id
      code
      name
      description
      isEligible
      eligibilityMessage
    }
  }
`;

export const COMPLETE_DRAFT_ORDER_WITH_COD = gql`
  mutation CompleteDraftOrderWithCod($orderId: ID!) {
    completeDraftOrderWithCod(orderId: $orderId) {
      success
      order {
        id
        code
        state
        totalWithTax
        payments {
          id
          method
          amount
          state
          transactionId
        }
      }
      errorCode
      errorDetails
    }
  }
`;

export const CANCEL_PAYMENT_LINK = gql`
  mutation CancelPaymentLink($id: ID!) {
    cancelPaymentLink(id: $id) {
      success
      paymentLink {
        ...PaymentLinkFields
      }
      errorCode
      message
    }
  }
  ${PAYMENT_LINK_FRAGMENT}
`;

export const REGENERATE_PAYMENT_LINK = gql`
  mutation RegeneratePaymentLink($orderId: ID!, $expiresInHours: Int) {
    regeneratePaymentLink(orderId: $orderId, expiresInHours: $expiresInHours) {
      success
      paymentLink {
        ...PaymentLinkFields
      }
      errorCode
      message
    }
  }
  ${PAYMENT_LINK_FRAGMENT}
`;

export const SEND_PAYMENT_LINK_SMS = gql`
  mutation SendPaymentLinkSms($id: ID!) {
    sendPaymentLinkSms(id: $id) {
      success
      errorCode
      message
    }
  }
`;

export const CREATE_DRAFT_ORDER = gql`
  mutation CreateDraftOrder {
    createDraftOrder {
      ... on Order {
        id
        code
        state
        total
        totalWithTax
        currencyCode
      }
    }
  }
`;

export const ADD_ITEM_TO_DRAFT_ORDER = gql`
  mutation AddItemToDraftOrder($orderId: ID!, $input: AddItemToDraftOrderInput!) {
    addItemToDraftOrder(orderId: $orderId, input: $input) {
      ... on Order {
        id
        code
        state
        total
        totalWithTax
        lines {
          id
          quantity
          unitPriceWithTax
          linePriceWithTax
          productVariant {
            id
            name
            sku
            featuredAsset {
              preview
            }
          }
        }
      }
      ... on OrderModificationError {
        errorCode
        message
      }
      ... on OrderLimitError {
        errorCode
        message
        maxItems
      }
      ... on NegativeQuantityError {
        errorCode
        message
      }
      ... on InsufficientStockError {
        errorCode
        message
        quantityAvailable
      }
    }
  }
`;

export const ADD_ITEM_TO_DRAFT_ORDER_WITH_CUSTOMIZATIONS = gql`
  mutation AddItemToDraftOrderWithCustomizations(
    $orderId: ID!
    $productVariantId: ID!
    $quantity: Int!
    $customizations: [CustomizationSelectionInput!]
  ) {
    addItemToDraftOrderWithCustomizations(
      orderId: $orderId
      productVariantId: $productVariantId
      quantity: $quantity
      customizations: $customizations
    ) {
      ... on Order {
        id
        code
        state
        total
        totalWithTax
        lines {
          id
          quantity
          unitPriceWithTax
          linePriceWithTax
          productVariant {
            id
            name
            sku
            featuredAsset {
              preview
            }
          }
        }
      }
      ... on OrderModificationError {
        errorCode
        message
      }
      ... on OrderLimitError {
        errorCode
        message
        maxItems
      }
      ... on NegativeQuantityError {
        errorCode
        message
      }
      ... on InsufficientStockError {
        errorCode
        message
        quantityAvailable
      }
    }
  }
`;

export const PREPARE_ORDER_LINE_CUSTOMIZATIONS = gql`
  query PrepareOrderLineCustomizations(
    $productVariantId: ID!
    $customizations: [CustomizationSelectionInput!]
  ) {
    prepareOrderLineCustomizations(productVariantId: $productVariantId, customizations: $customizations) {
      serializedCustomizations
      totalPriceImpact
      selectedCustomizations {
        fieldId
        fieldType
        fieldLabel
        optionId
        optionName
        textInput
        fileAssetId
        fileAssetPreview
        price
      }
    }
  }
`;

export const ADJUST_DRAFT_ORDER_LINE = gql`
  mutation AdjustDraftOrderLine($orderId: ID!, $input: AdjustDraftOrderLineInput!) {
    adjustDraftOrderLine(orderId: $orderId, input: $input) {
      ... on Order {
        id
        total
        totalWithTax
        lines {
          id
          quantity
          unitPriceWithTax
          linePriceWithTax
        }
      }
      ... on OrderModificationError {
        errorCode
        message
      }
      ... on OrderLimitError {
        errorCode
        message
      }
      ... on NegativeQuantityError {
        errorCode
        message
      }
      ... on InsufficientStockError {
        errorCode
        message
      }
    }
  }
`;

export const REMOVE_DRAFT_ORDER_LINE = gql`
  mutation RemoveDraftOrderLine($orderId: ID!, $orderLineId: ID!) {
    removeDraftOrderLine(orderId: $orderId, orderLineId: $orderLineId) {
      ... on Order {
        id
        total
        totalWithTax
        lines {
          id
          quantity
        }
      }
      ... on OrderModificationError {
        errorCode
        message
      }
    }
  }
`;

export const SET_CUSTOMER_FOR_DRAFT_ORDER = gql`
  mutation SetCustomerForDraftOrder($orderId: ID!, $customerId: ID, $input: CreateCustomerInput) {
    setCustomerForDraftOrder(orderId: $orderId, customerId: $customerId, input: $input) {
      ... on Order {
        id
        customer {
          id
          firstName
          lastName
          emailAddress
          phoneNumber
        }
      }
      ... on EmailAddressConflictError {
        errorCode
        message
      }
    }
  }
`;

export const SET_DRAFT_ORDER_SHIPPING_ADDRESS = gql`
  mutation SetDraftOrderShippingAddress($orderId: ID!, $input: CreateAddressInput!) {
    setDraftOrderShippingAddress(orderId: $orderId, input: $input) {
      ... on Order {
        id
        shippingAddress {
          fullName
          company
          streetLine1
          streetLine2
          city
          province
          postalCode
          country
          phoneNumber
        }
      }
    }
  }
`;

export const SET_DRAFT_ORDER_BILLING_ADDRESS = gql`
  mutation SetDraftOrderBillingAddress($orderId: ID!, $input: CreateAddressInput!) {
    setDraftOrderBillingAddress(orderId: $orderId, input: $input) {
      ... on Order {
        id
        billingAddress {
          fullName
          company
          streetLine1
          streetLine2
          city
          province
          postalCode
          country
          phoneNumber
        }
      }
    }
  }
`;

export const SET_DRAFT_ORDER_SHIPPING_METHOD = gql`
  mutation SetDraftOrderShippingMethod($orderId: ID!, $shippingMethodId: ID!) {
    setDraftOrderShippingMethod(orderId: $orderId, shippingMethodId: $shippingMethodId) {
      ... on Order {
        id
        shippingWithTax
        shippingLines {
          shippingMethod {
            id
            name
          }
        }
      }
      ... on OrderModificationError {
        errorCode
        message
      }
      ... on IneligibleShippingMethodError {
        errorCode
        message
      }
      ... on NoActiveOrderError {
        errorCode
        message
      }
    }
  }
`;

export const APPLY_COUPON_CODE_TO_DRAFT_ORDER = gql`
  mutation ApplyCouponCodeToDraftOrder($orderId: ID!, $couponCode: String!) {
    applyCouponCodeToDraftOrder(orderId: $orderId, couponCode: $couponCode) {
      ... on Order {
        id
        couponCodes
        discounts {
          type
          description
          amount
        }
        totalWithTax
      }
      ... on CouponCodeInvalidError {
        errorCode
        message
        couponCode
      }
      ... on CouponCodeExpiredError {
        errorCode
        message
        couponCode
      }
      ... on CouponCodeLimitError {
        errorCode
        message
        couponCode
        limit
      }
    }
  }
`;

export const UNSET_DRAFT_ORDER_SHIPPING_ADDRESS = gql`
  mutation UnsetDraftOrderShippingAddress($orderId: ID!) {
    unsetDraftOrderShippingAddress(orderId: $orderId) {
      id
    }
  }
`;

export const UNSET_DRAFT_ORDER_BILLING_ADDRESS = gql`
  mutation UnsetDraftOrderBillingAddress($orderId: ID!) {
    unsetDraftOrderBillingAddress(orderId: $orderId) {
      id
    }
  }
`;

export const DELETE_DRAFT_ORDER = gql`
  mutation DeleteDraftOrder($orderId: ID!) {
    deleteDraftOrder(orderId: $orderId) {
      result
      message
    }
  }
`;

export const GET_ELIGIBLE_SHIPPING_METHODS = gql`
  query GetEligibleShippingMethods($orderId: ID!) {
    eligibleShippingMethodsForDraftOrder(orderId: $orderId) {
      id
      name
      code
      description
      price
      priceWithTax
      metadata
    }
  }
`;

export const ADD_MANUAL_PAYMENT_TO_ORDER = gql`
  mutation AddManualPaymentToOrder($input: ManualPaymentInput!) {
    addManualPaymentToOrder(input: $input) {
      ... on Order {
        id
        state
        payments {
          id
          method
          amount
          state
          transactionId
        }
      }
      ... on ManualPaymentStateError {
        errorCode
        message
      }
    }
  }
`;

export const SETTLE_REFUND = gql`
  mutation SettleRefund($input: SettleRefundInput!) {
    settleRefund(input: $input) {
      ... on Refund {
        id
        state
        transactionId
        total
        reason
        metadata
      }
      ... on RefundStateTransitionError {
        errorCode
        message
        transitionError
        fromState
        toState
      }
    }
  }
`;

export const SETTLE_MANUAL_REFUND = gql`
  mutation SettleManualRefund($input: SettleManualRefundInput!) {
    settleManualRefund(input: $input) {
      id
      state
      transactionId
      total
      reason
      metadata
    }
  }
`;

export const GET_ORDER_FOR_MODIFY = gql`
  query GetOrderForModify($id: ID!) {
    order(id: $id) {
      id
      code
      state
      orderPlacedAt
      totalWithTax
      currencyCode
      shippingAddress {
        fullName
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        country
        countryCode
        phoneNumber
      }
      billingAddress {
        fullName
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        country
        countryCode
        phoneNumber
      }
      lines {
        id
        quantity
        unitPriceWithTax
        selectedCustomizations {
          fieldId
          fieldType
          fieldLabel
          optionId
          optionName
          textInput
          fileAssetId
          fileAssetPreview
          price
        }
        productVariant {
          id
          name
          sku
          featuredAsset {
            preview
          }
        }
      }
      shippingLines {
        shippingMethod {
          id
        }
      }
    }
  }
`;

const GOVERNED_ORDER_EDIT_RESULT = gql`
  fragment GovernedOrderEditResultFields on GovernedOrderEditResult {
    success
    errorCode
    errorDetails
    beforeFingerprint
    afterFingerprint
    priceDelta
    edit {
      editKey
      status
      kind
      beforeFingerprint
      afterFingerprint
      priceDelta
      currencyCode
      nativeModificationId
      settlementAttemptKey
      refundIntentId
      createdAt
      updatedAt
    }
    order {
      id
      code
      state
      total
      totalWithTax
      currencyCode
      payments {
        id
        method
        state
        metadata
      }
    }
  }
`;

export const PREVIEW_GOVERNED_ORDER_EDIT = gql`
  mutation PreviewGovernedOrderEdit($orderId: ID!, $changes: GovernedOrderEditChangesInput!) {
    previewGovernedOrderEdit(orderId: $orderId, changes: $changes) {
      ...GovernedOrderEditResultFields
    }
  }
  ${GOVERNED_ORDER_EDIT_RESULT}
`;

export const APPLY_GOVERNED_ORDER_EDIT = gql`
  mutation ApplyGovernedOrderEdit($input: ApplyGovernedOrderEditInput!) {
    applyGovernedOrderEdit(input: $input) {
      ...GovernedOrderEditResultFields
    }
  }
  ${GOVERNED_ORDER_EDIT_RESULT}
`;

export const CANCEL_GOVERNED_ORDER_EDIT = gql`
  mutation CancelGovernedOrderEdit($editKey: String!) {
    cancelGovernedOrderEdit(editKey: $editKey) {
      ...GovernedOrderEditResultFields
    }
  }
  ${GOVERNED_ORDER_EDIT_RESULT}
`;

export const REMOVE_COUPON_CODE_FROM_DRAFT_ORDER = gql`
  mutation RemoveCouponCodeFromDraftOrder($orderId: ID!, $couponCode: String!) {
    removeCouponCodeFromDraftOrder(orderId: $orderId, couponCode: $couponCode) {
      ... on Order {
        id
        couponCodes
        discounts {
          type
          description
          amount
        }
        totalWithTax
      }
    }
  }
`;

export const ADD_SURCHARGE_TO_ORDER = gql`
  mutation AddSurchargeToOrder($orderId: ID!, $input: SurchargeInput!) {
    addSurchargeToOrder(orderId: $orderId, input: $input) {
      ... on Order {
        id
        surcharges {
          id
          description
          sku
          price
          priceWithTax
          taxRate
        }
        total
        totalWithTax
      }
    }
  }
`;

export const APPLY_MANUAL_DRAFT_DISCOUNT = gql`
  mutation ApplyManualDraftDiscount($input: ApplyManualDraftDiscountInput!) {
    applyManualDraftDiscount(input: $input) {
      id
      couponCodes
      discounts {
        adjustmentSource
        amount
        amountWithTax
        description
        type
      }
      total
      totalWithTax
      subTotal
      subTotalWithTax
    }
  }
`;

export const REMOVE_MANUAL_DRAFT_DISCOUNT = gql`
  mutation RemoveManualDraftDiscount($orderId: ID!) {
    removeManualDraftDiscount(orderId: $orderId) {
      id
      couponCodes
      discounts {
        adjustmentSource
        amount
        amountWithTax
        description
        type
      }
      total
      totalWithTax
      subTotal
      subTotalWithTax
    }
  }
`;

export const REMOVE_SURCHARGE_FROM_ORDER = gql`
  mutation RemoveSurchargeFromOrder($surchargeId: ID!) {
    removeSurchargeFromOrder(surchargeId: $surchargeId) {
      id
      surcharges {
        id
        description
        sku
        price
        priceWithTax
        taxRate
      }
      total
      totalWithTax
    }
  }
`;

export const UPDATE_ORDER_CUSTOM_FIELDS = gql`
  mutation SetOrderCustomFields($input: UpdateOrderInput!) {
    setOrderCustomFields(input: $input) {
      ... on Order {
        id
        customFields {
          tags
        }
      }
    }
  }
`;

export const SET_ORDER_PICKUP_LOCATION = gql`
  mutation SetOrderPickupLocation($orderId: ID!, $pickupLocationId: ID) {
    setOrderPickupLocation(orderId: $orderId, pickupLocationId: $pickupLocationId) {
      success
      errorCode
      errorDetails
      order {
        id
        customFields {
          pickupLocationId
        }
      }
    }
  }
`;

export const EXPORT_TEMPLATES = gql`
  query ExportTemplates($type: String!) {
    exportTemplates(type: $type) {
      id
      name
      type
      columns
      createdAt
    }
  }
`;

export const ORDER_COLUMN_DEFINITIONS = gql`
  query OrderColumnDefinitions {
    orderColumnDefinitions {
      field
      headerEn
      headerAr
      defaultIncluded
    }
  }
`;

export const CREATE_EXPORT_TEMPLATE = gql`
  mutation CreateExportTemplate($input: CreateExportTemplateInput!) {
    createExportTemplate(input: $input) {
      id
      name
      columns
    }
  }
`;

export const UPDATE_EXPORT_TEMPLATE = gql`
  mutation UpdateExportTemplate($id: ID!, $input: UpdateExportTemplateInput!) {
    updateExportTemplate(id: $id, input: $input) {
      id
      name
      columns
    }
  }
`;

export const DELETE_EXPORT_TEMPLATE = gql`
  mutation DeleteExportTemplate($id: ID!) {
    deleteExportTemplate(id: $id) {
      result
    }
  }
`;

// Task 44: read-only explanation of applied and lost promotions on an order.
const PROMOTION_EXPLAIN_ENTRY_FRAGMENT = gql`
  fragment PromotionExplainEntryFields on PromotionExplainEntry {
    promotionId
    name
    couponCode
    combinationClass
    priority
    reason
    inferred
    winnerPromotionIds
    amount
    amountWithTax
    lines {
      orderLineId
      amount
      amountWithTax
    }
    shippingLines {
      shippingLineId
      amount
      amountWithTax
    }
  }
`;

export const GET_ORDER_PROMOTION_EXPLAIN = gql`
  query OrderPromotionExplain($orderId: ID!) {
    orderPromotionExplain(orderId: $orderId) {
      orderId
      currencyCode
      basis
      referenceTime
      totalDiscount
      totalDiscountWithTax
      applied {
        ...PromotionExplainEntryFields
      }
      lost {
        ...PromotionExplainEntryFields
      }
    }
  }
  ${PROMOTION_EXPLAIN_ENTRY_FRAGMENT}
`;
